import { observer } from "mobx-react-lite";
import React from "react";
import { Container, Form, Grid, Header, Table } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useStore } from "../../../app/stores/store";
import PageHeader from "../../../app/layout/PageHeader";
import WholeNumberInput from "../../../app/common/form/WholeNumberInput";
import { Formik } from "formik";

interface Props {
  startDate: Date;
  endDate: Date;
}

export default observer(function InventoryCountSheetPrint({
  startDate,
  endDate,
}: Props) {
  const { t } = useTranslation(["common", "reports"]);
  const { inventoryPhysicalCountReportStore } = useStore();
  const {
    groupedInventoryPhysicalCountReport,
    getInventoryPhysicalCountReport,
  } = inventoryPhysicalCountReportStore;

  return (
    <>
      <Formik
        initialValues={
          getInventoryPhysicalCountReport &&
          getInventoryPhysicalCountReport.length
            ? (getInventoryPhysicalCountReport.reduce(
                (ar: any, r) => {
                  ar[r.id ?? ""] = ""; // Correctly assign the value to the accumulator
                  return ar; // Ensure the accumulator is returned
                },
                { "": "0" } // Initial accumulator object
              ) as { [key: string]: number })
            : {}
        }
        onSubmit={(_) => console.log("submitted")}
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <Form>
            <Container className="page">
              <Grid stackable columns={2} className="headerPrint">
                <Grid.Row>
                  <Grid.Column>
                    <PageHeader
                      header={`${t("report", { ns: "reports" })}: ${t(
                        "physical_count_sheet",
                        {
                          ns: "reports",
                        }
                      )}`}
                      type="h2"
                      divider={false}
                      getAlerts={true}
                      addTitle={true}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <Header
                      content={`${format(new Date(), "PP")}`}
                      as="h2"
                      textAlign="right"
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              {groupedInventoryPhysicalCountReport &&
                groupedInventoryPhysicalCountReport.length > 0 && (
                  <Table striped unstackable className="dnxTable">
                    {groupedInventoryPhysicalCountReport.map(
                      ([group, items], groupIndex) => (
                        <React.Fragment key={groupIndex}>
                          <>
                            <Table.Body>
                              <Table.Row>
                                <Table.Cell
                                  colSpan={16}
                                  textAlign="left"
                                  className="terms_bold"
                                >
                                  {group}
                                </Table.Cell>
                              </Table.Row>
                            </Table.Body>
                            <Table.Header>
                              <Table.Row>
                                <Table.HeaderCell>
                                  {t("part_number", { ns: "reports" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                  {t("description", { ns: "common" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">
                                  {t("pops.qty", { ns: "tickets" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">
                                  {t("physical_count", { ns: "reports" })}
                                </Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {items.map((item, itemIndex) => (
                                <Table.Row key={item.id}>
                                  <Table.Cell
                                    data-label={`${t("part_number", {
                                      ns: "reports",
                                    })}:`}
                                  >
                                    {item.partNumber}
                                  </Table.Cell>
                                  <Table.Cell
                                    data-label={`${t("description", {
                                      ns: "common",
                                    })}:`}
                                  >
                                    {item.partDescription}
                                  </Table.Cell>
                                  <Table.Cell
                                    textAlign="right"
                                    data-label={`${t("pops.qty", {
                                      ns: "tickets",
                                    })}:`}
                                  >
                                    {item.quantity}
                                  </Table.Cell>
                                  <Table.Cell
                                    data-label={`${t("physical_count", {
                                      ns: "reports",
                                    })}:`}
                                    textAlign="right"
                                  >
                                    <WholeNumberInput
                                      disabled
                                      name={item.id ?? ""}
                                      placeholder=" "
                                      className={`${
                                        Number(values[item.id ?? ""]) < 0 &&
                                        "negative-input"
                                      }`}
                                      maxLength={500}
                                    />
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                            </Table.Body>
                          </>
                        </React.Fragment>
                      )
                    )}
                  </Table>
                )}
            </Container>
          </Form>
        )}
      </Formik>
    </>
  );
});
